import axios from 'axios';
import AuthUtils from './AuthUtils';
import refreshAccessToken from './TokenRefresher';
import store from '../store';
import { showLockScreen, } from '../actions/auth';

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Request interceptor to check token expiration before sending
axios.interceptors.request.use(
  async (config) => {
    const { auth = {}, } = store.getState();
    const { authentication = {}, } = auth;
    const { token, } = authentication;

    // Skip token check for login endpoint
    if (config.url?.includes('/login') || config.url?.includes('/ping')) {
      return config;
    }

    if (token && !AuthUtils.isTokenValid(token)) {
      // Token is expired, try to refresh
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const newToken = await refreshAccessToken();
          isRefreshing = false;
          processQueue(null, newToken);

          // Update current request with new token
          return {
            ...config,
            headers: {
              ...config.headers,
              Authorization: newToken,
            },
          };
        } catch (error) {
          isRefreshing = false;
          processQueue(error, null);
          // Show lock screen instead of logging out
          store.dispatch(showLockScreen());
          return Promise.reject(error);
        }
      } else {
        // Wait for the ongoing refresh to complete
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject, });
        }).then(newToken => {
          return {
            ...config,
            headers: {
              ...config.headers,
              Authorization: newToken,
            },
          };
        }).catch(err => {
          return Promise.reject(err);
        });
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle 401 errors
axios.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // 401 means the token has expired or is invalid
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Wait for the ongoing refresh to complete
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject, });
        }).then(token => {
          originalRequest.headers.Authorization = token;
          originalRequest._retry = true;
          return axios(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await refreshAccessToken();
        isRefreshing = false;
        processQueue(null, newToken);

        // Retry original request with new token
        originalRequest.headers.Authorization = newToken;
        return axios(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        processQueue(refreshError, null);
        // Show lock screen instead of logging out
        store.dispatch(showLockScreen());
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default axios;
